import ChevronRightSvg from '@remote-com/norma/icons/chevron-right.svg';
import { Button } from 'remote-com-shared/components/Button';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fluidProp, whenProp } from 'remote-com-shared/style/helpers';
import { sharedTransition } from 'remote-com-shared/style/motion';
import styled, { css } from 'styled-components';

import { fromTabletBreakpoint, tabletBreakpoint } from '@/src/domains/shared/breakpoints';

export const ChevronIcon = styled(ChevronRightSvg)`
  height: 14px;
  width: 14px;
`;

export const SectionWrapper = styled.div`
  ${fluidProp(['padding-top', 'padding-bottom'], '80px', '140px')}
  --titleColor: inherit;
  --textColor: var(--colors-lynch);
  position: relative;

  ${({ $tone }) => {
    switch ($tone) {
      case 'light':
        return css`
          background-color: var(--colors-blank);
        `;
      case 'purpleMinsk':
        return css`
          background-image: var(--colors-purpleMinsk);
        `;
      default:
        return css`
          background: var(--colors-zircon);
          background-attachment: fixed;
        `;
    }
  }}
`;

const SectionCenteredContent = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

export const SectionHeader = styled.header`
  ${SectionCenteredContent}
`;

export const SectionFooter = styled.footer`
  ${SectionCenteredContent}
`;

export const darkTitle = styled.div`
  color: var(--colors-darkBlue);
`;

export const SectionTitle = styled.div`
  ${fluidProp('font-size', '24px', '36px')}
  line-height: 1.2em;
  font-weight: 600;
  margin-bottom: 20px;

  ${whenProp(
    '$centered',
    css`
      text-align: center;
    `
  )}

  ${({ $tone }) => {
    switch ($tone) {
      case 'light':
        return css`
          color: var(--colors-blank);
        `;
      default:
        return css`
          color: var(--colors-darkBlue);
        `;
    }
  }}

  * {
    font-size: inherit;
    line-height: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

const RichTextBaseStyle = css`
  --textHighlightSecondary: currentColor;
  // --textHighlightColor
  // --textHighlightSecondaryOpacity

  strong {
    font-weight: 600;
  }

  a {
    position: relative;
    color: var(--textHighlightColor);

    &::after {
      content: '';
      position: absolute;
      border-bottom-width: 1px;
      border-bottom-style: solid;
      border-bottom-color: var(--textHighlightSecondary);
      opacity: var(--textHighlightSecondaryOpacity);
      bottom: -3px;
      left: 0;
      right: 0;
      pointer-events: none;
    }

    &:hover,
    &:focus {
      &::after {
        border-bottom-width: 2px;
      }
    }
  }

  ul {
    list-style-type: '- ';
    list-style-position: inside;
  }
`;

export const Description = styled.div`
  font-size: 18px;
  line-height: 1.4em;

  ${({ $tone }) => {
    switch ($tone) {
      case 'dark':
        return css`
          color: var(--colors-bayoux);
        `;
      case 'light':
        return css`
          color: var(--colors-moonRaker);
        `;
      default:
        return css`
          color: var(--textColor);
        `;
    }
  }}

  * {
    font-size: inherit;
    line-height: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const SectionDescription = styled(Description)`
  ${RichTextBaseStyle}
  --textHighlightColor: var(--colors-irisBlue);
  --textHighlightSecondaryOpacity: 0.3;
  max-width: 838px;
`;

export const FormWrapper = styled.div`
  margin-top: 0px;
  margin-bottom: -50px;
  z-index: 1;

  ${fromTabletBreakpoint(css`
    margin-top: -200px;
    margin-bottom: -200px;
  `)}
`;

export const GatedContentSectionWrapper = styled(SectionWrapper)`
  --textColor: var(--colors-spindle);
  background: var(--colors-zircon);

  ${ContentContainer} {
    display: grid;
    gap: 50px;
    align-items: center;

    ${tabletBreakpoint(css`
      grid-template-rows: 1fr min-content;
    `)}
    ${fromTabletBreakpoint(css`
      grid-template-columns: 2fr minmax(400px, 3fr);
    `)}
  }
`;

export const MainContent = styled.div`
  ${SectionTitle} {
    margin-bottom: 30px;
  }

  ${tabletBreakpoint(css`
    text-align: center;
  `)}
`;
export const FormButtonsWrapper = styled.div`
  width: max-content;
  margin: 0 auto;
  text-align: center;
  min-height: 44px;

  display: flex;
  gap: 1rem;
`;

export const StepCounterWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;
export const StepCounter = styled.p`
  margin: 0;
  color: var(--colors-lynch);
  ${({ theme }) => theme.typography.body};
`;

export const StyledButton = styled(Button).attrs({ type: 'button', shadow: true })`
  ${whenProp(
    '$primary',
    css`
      && {
        background-color: var(--newBrandColor);
      }
    `
  )}

  ${ChevronIcon} {
    ${sharedTransition(`transform`)}
    margin-left: 5px;
    margin-right: -10px;
  }

  :hover {
    ${ChevronIcon} {
      transform: translateX(30%);
    }
  }
`;

export const SubmitButton = styled(Button).attrs({
  shadow: true,
  type: 'submit',
})`
  && {
    background-color: var(--newBrandColor);
  }
  &:disabled {
    background-color: var(--colors-spindle);
    cursor: not-allowed;
  }
`;

export const FormLabel = styled.span`
  ${({ theme }) => theme.typography.bodyXS}
  line-height: 20px;
  color: var(--colors-bayoux);

  a {
    color: var(--colors-irisBlue);
  }
`;

export const RecommendAdvocateSectionWrapper = styled(SectionWrapper)``;

export const IntroduceSectionWrapper = styled(SectionWrapper)`
  ${tabletBreakpoint(css`
    padding-top: 150px;
  `)}
`;

export const PrimaryButton = styled(Button).attrs({
  tone: 'primary',
  shadow: true,
})``;
